/**
 * Compresses an image File on the client side before storing as a base64 data URL.
 * - Accepts heavy source files (up to ~25 MB by default)
 * - Downscales to a max dimension (default 1600 px) preserving aspect ratio
 * - Encodes as JPEG (or PNG if alpha is detected) and adapts quality to target size
 *
 * @param {File} file
 * @param {Object} [opts]
 * @param {number} [opts.maxSourceBytes=25*1024*1024]  - reject files larger than this
 * @param {number} [opts.maxDim=1600]                  - max width/height in pixels
 * @param {number} [opts.targetBytes=800*1024]         - aim to keep output under this size
 * @param {number} [opts.minQuality=0.5]
 * @param {number} [opts.startQuality=0.85]
 * @returns {Promise<string>} base64 data URL ready to store
 */
export async function compressImage(file, opts = {}) {
  const {
    maxSourceBytes = 25 * 1024 * 1024,
    maxDim = 1600,
    targetBytes = 800 * 1024,
    minQuality = 0.5,
    startQuality = 0.85,
  } = opts;

  if (!file) throw new Error('No se recibió un archivo');
  const isHeic = /\.heic$|\.heif$/i.test(file.name) || file.type === 'image/heic' || file.type === 'image/heif';
  if (!isHeic && (!file.type || !file.type.startsWith('image/'))) {
    throw new Error('El archivo debe ser una imagen');
  }
  if (file.size > maxSourceBytes) {
    const mb = (maxSourceBytes / (1024 * 1024)).toFixed(0);
    throw new Error(`La imagen es demasiado grande (máx ${mb} MB)`);
  }

  // Android/Chrome bug: <input type="file" capture> puede devolver el mismo
  // URI cacheado del media provider entre capturas consecutivas. Leemos el
  // archivo a ArrayBuffer y lo envolvemos en un Blob/File fresco para romper
  // cualquier caché del content-URI antes de continuar el pipeline.
  try {
    const buf = await file.arrayBuffer();
    file = new File([new Blob([buf], { type: file.type || 'image/jpeg' })],
      file.name || 'foto.jpg',
      { type: file.type || 'image/jpeg', lastModified: file.lastModified || Date.now() }
    );
  } catch (_) {
    // Si el navegador no soporta arrayBuffer/File constructor, seguimos con el archivo original.
  }

  // HEIC/HEIF (iPhone): los navegadores no los renderizan nativamente.
  // Convertimos a JPEG en el navegador con heic2any antes de continuar.
  let workingFile = file;
  if (isHeic) {
    const heic2any = (await import('heic2any')).default;
    const blob = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 });
    workingFile = blob instanceof Blob ? blob : (Array.isArray(blob) ? blob[0] : blob);
  }

  // Load the file into an HTMLImageElement
  const dataUrl = await new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = () => reject(new Error('No se pudo leer el archivo'));
    fr.readAsDataURL(workingFile);
  });
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error('Imagen no válida'));
    i.src = dataUrl;
  });

  // Compute target size
  let { width, height } = img;
  if (width > maxDim || height > maxDim) {
    const scale = Math.min(maxDim / width, maxDim / height);
    width = Math.round(width * scale);
    height = Math.round(height * scale);
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, width, height);

  // PNG keeps transparency; JPEG is much smaller for photos.
  // HEIC siempre se exporta como JPEG (no tiene transparencia).
  const isPng = !isHeic && file.type === 'image/png';
  if (isPng) {
    // PNG can't be quality-compressed; export once and return.
    return canvas.toDataURL('image/png');
  }

  // Iteratively reduce quality until we fit within target
  let quality = startQuality;
  let out = canvas.toDataURL('image/jpeg', quality);
  // Approximate decoded byte size from base64 length
  const sizeOf = (du) => Math.ceil((du.length - du.indexOf(',') - 1) * 3 / 4);
  while (sizeOf(out) > targetBytes && quality > minQuality) {
    quality = Math.max(minQuality, quality - 0.1);
    out = canvas.toDataURL('image/jpeg', quality);
  }
  return out;
}
